import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { getBusinessProfile, getBusinessClients, getBusinessProjects, getBusinessEmployees } from '../../services/businessService';
import type { BusinessData, ClientData, ProjectData, EmployeeData } from '../../services/businessService';
import { FaUserFriends, FaProjectDiagram, FaUserTie, FaBell, FaCheck, FaTimes, FaExchangeAlt, FaTimesCircle } from 'react-icons/fa';
import { getFirestore, collection, query, where, onSnapshot, updateDoc, doc, addDoc, getDoc } from 'firebase/firestore';
import { useNavigate, Link } from 'react-router-dom';

interface TaskCompletion {
  completed: number;
  total: number;
  percentage: number;
}

// Componente para mostrar notificaciones individuales
const NotificationItem: React.FC<{ notification: any; onClose: () => void }> = ({ notification, onClose }) => {
  const [showCounterModal, setShowCounterModal] = useState(false);
  const [counterPrice, setCounterPrice] = useState('');
  const [loading, setLoading] = useState(false);

  const handleAccept = async () => {
    if (!notification.assignmentRequestId) return;
    setLoading(true);
    try {
      const db = getFirestore();
      await updateDoc(doc(db, 'assignmentRequests', notification.assignmentRequestId), { 
        status: 'accepted', 
        updatedAt: new Date().toISOString() 
      });
      
      // Notificar al worker
      await addDoc(collection(db, 'notifications', notification.workerId, 'notifications'), {
        type: 'assignmentAccepted',
        messageKey: 'notifications.assignmentAccepted',
        messageParams: {
          taskTitle: notification.messageParams?.taskTitle,
          businessId: notification.businessId,
          workerId: notification.workerId
        },
        assignmentRequestId: notification.assignmentRequestId,
        businessId: notification.businessId,
        workerId: notification.workerId,
        createdAt: new Date().toISOString(),
        read: false
      });
      
      onClose();
    } catch (error) {
      console.error('Error accepting offer:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!notification.assignmentRequestId) return;
    setLoading(true);
    try {
      const db = getFirestore();
      await updateDoc(doc(db, 'assignmentRequests', notification.assignmentRequestId), { 
        status: 'rejected', 
        updatedAt: new Date().toISOString() 
      });
      
      // Notificar al worker
      await addDoc(collection(db, 'notifications', notification.workerId, 'notifications'), {
        type: 'assignmentRejected',
        messageKey: 'notifications.assignmentRejected',
        messageParams: {
          taskTitle: notification.messageParams?.taskTitle,
          businessId: notification.businessId,
          workerId: notification.workerId
        },
        assignmentRequestId: notification.assignmentRequestId,
        businessId: notification.businessId,
        workerId: notification.workerId,
        createdAt: new Date().toISOString(),
        read: false
      });
      
      onClose();
    } catch (error) {
      console.error('Error rejecting offer:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCounter = async () => {
    if (!notification.assignmentRequestId || !counterPrice) return;
    setLoading(true);
    try {
      const db = getFirestore();
      const price = Number(counterPrice);
      const duration = notification.messageParams?.duration || 1;
      const total = price * duration;
      
      await updateDoc(doc(db, 'assignmentRequests', notification.assignmentRequestId), {
        status: 'counter',
        counterPrice: price,
        total: total,
        updatedAt: new Date().toISOString(),
        lastCounterBy: 'business'
      });
      
      // Notificar al worker
      await addDoc(collection(db, 'notifications', notification.workerId, 'notifications'), {
        type: 'assignmentCounter',
        messageKey: 'notifications.assignmentCounter',
        messageParams: {
          taskTitle: notification.messageParams?.taskTitle,
          businessId: notification.businessId,
          workerId: notification.workerId,
          counterPrice: price,
          total: total
        },
        assignmentRequestId: notification.assignmentRequestId,
        businessId: notification.businessId,
        workerId: notification.workerId,
        createdAt: new Date().toISOString(),
        read: false
      });
      
      setShowCounterModal(false);
      onClose();
    } catch (error) {
      console.error('Error sending counter offer:', error);
    } finally {
      setLoading(false);
    }
  };

  const getNotificationContent = () => {
    switch (notification.type) {
      case 'assignmentResponse':
        return (
          <div className="p-3 border-b border-gray-100 last:border-b-0">
            <div className="flex justify-between items-start mb-2">
              <h4 className="font-medium text-gray-900 text-sm">
                {notification.messageParams?.taskTitle || 'Nueva oferta de trabajo'}
              </h4>
              <span className="text-xs text-gray-500">
                {new Date(notification.createdAt).toLocaleDateString()}
              </span>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              {notification.messageParams?.workerName || 'Worker'} ha respondido a tu oferta
            </p>
            {notification.messageParams?.counterPrice && (
              <div className="bg-blue-50 p-2 rounded mb-3">
                <p className="text-sm text-blue-800">
                  <strong>Contraoferta:</strong> ${notification.messageParams.counterPrice} 
                  {notification.messageParams.priceUnit === 'hour' ? '/hora' : '/día'}
                </p>
                <p className="text-xs text-blue-600">
                  Total: ${notification.messageParams.total || (notification.messageParams.counterPrice * (notification.messageParams.duration || 1))}
                </p>
              </div>
            )}
            <div className="flex gap-2">
              <button
                onClick={handleAccept}
                disabled={loading}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white text-xs py-1 px-2 rounded transition-colors disabled:opacity-50"
              >
                <FaCheck className="inline mr-1" />
                Aceptar
              </button>
              <button
                onClick={handleReject}
                disabled={loading}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white text-xs py-1 px-2 rounded transition-colors disabled:opacity-50"
              >
                <FaTimes className="inline mr-1" />
                Rechazar
              </button>
              <button
                onClick={() => setShowCounterModal(true)}
                disabled={loading}
                className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white text-xs py-1 px-2 rounded transition-colors disabled:opacity-50"
              >
                <FaExchangeAlt className="inline mr-1" />
                Contraoferta
              </button>
            </div>
          </div>
        );
      default:
        return (
          <div className="p-3 border-b border-gray-100 last:border-b-0">
            <p className="text-sm text-gray-600">
              {notification.message || 'Notificación'}
            </p>
          </div>
        );
    }
  };

  return (
    <>
      {getNotificationContent()}
      
      {/* Modal de contraoferta */}
      {showCounterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="text-lg font-semibold mb-4">Enviar contraoferta</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Precio por {notification.messageParams?.priceUnit === 'hour' ? 'hora' : 'día'}</label>
              <input
                type="number"
                value={counterPrice}
                onChange={(e) => setCounterPrice(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                placeholder="Ingresa el precio"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowCounterModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancelar
              </button>
              <button
                onClick={handleCounter}
                disabled={!counterPrice || loading}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md disabled:opacity-50"
              >
                {loading ? 'Enviando...' : 'Enviar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const BusinessDashboard: React.FC = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [businessData, setBusinessData] = useState<BusinessData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const bellRef = useRef<HTMLButtonElement>(null);
  const [clients, setClients] = useState<ClientData[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientsError, setClientsError] = useState<string | null>(null);
  const [projects, setProjects] = useState<ProjectData[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState<string | null>(null);
  const [employees, setEmployees] = useState<EmployeeData[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [workers, setWorkers] = useState<any[]>([]);
  const [selectedPerson, setSelectedPerson] = useState<any | null>(null);
  const [personAssignments, setPersonAssignments] = useState<any[]>([]);
  const [modalOpen, setModalOpen] = useState(false);

  // Cálculo de resumen financiero basado en los proyectos del negocio logueado
  const financialData = useMemo(() => {
    // Ingresos: suma de subtotalWithDiscounts o totalAmount de todos los proyectos
    const income = projects.reduce((acc, project) => {
      const p = project as any;
      if (typeof p.subtotalWithDiscounts === 'number') {
        return acc + p.subtotalWithDiscounts;
      } else if (typeof p.totalAmount === 'number') {
        return acc + p.totalAmount;
      }
      return acc;
    }, 0);
    // Gastos: puedes ajustar esto si tienes un campo específico, por ahora lo dejamos en 0
    const expenses = 0;
    const balance = income - expenses;
    return {
      income,
      expenses,
      balance,
      period: 'Actual'
    };
  }, [projects]);

  // Cálculo de cumplimiento de tareas basado en los proyectos y sus tasks
  const taskCompletion: TaskCompletion = useMemo(() => {
    // Reunir todas las tareas de todos los proyectos
    const allTasks = projects.flatMap((p: any) => Array.isArray(p.tasks) ? p.tasks : []);
    const total = allTasks.length;
    const completed = allTasks.filter((t: any) => t.columnId === 'done' || t.status === 'completed').length;
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { completed, total, percentage };
  }, [projects]);

  useEffect(() => {
    const fetchBusinessData = async () => {
      try {
        if (currentUser?.uid) {
          const data = await getBusinessProfile(currentUser.uid);
          setBusinessData(data);
        }
      } catch (err) {
        setError(t('errors.fetchBusinessData'));
      } finally {
        setLoading(false);
      }
    };

    fetchBusinessData();
  }, [currentUser, t]);

  useEffect(() => {
    if (!businessData || !businessData.businessId) return;
    
    try {
    const db = getFirestore();
    const notifRef = collection(db, 'notifications', businessData.businessId, 'notifications');
    const q = query(notifRef, where('read', '==', false));
    const unsub = onSnapshot(q, snap => {
      setNotifications(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      }, (error) => {
        console.error('[BusinessDashboard] Error in notifications listener:', error);
        // Si hay error de permisos, no mostrar notificaciones
        if (error.code === 'permission-denied') {
          console.log('[BusinessDashboard] Permission denied for notifications');
        }
      });
      return () => unsub();
    } catch (error) {
      console.error('[BusinessDashboard] Error setting up notifications listener:', error);
    }
  }, [businessData?.businessId]);

  // useEffect para cargar clientes
  useEffect(() => {
    if (!businessData?.id) return;
    const fetchClients = async () => {
      setClientsLoading(true);
      setClientsError(null);
      try {
        const data = await getBusinessClients(businessData.id);
        setClients(data);
      } catch (err) {
        setClientsError(t('errors.fetchClients'));
      } finally {
        setClientsLoading(false);
      }
    };
    fetchClients();
  }, [businessData?.id, t]);

  // useEffect para cargar proyectos
  useEffect(() => {
    if (!businessData?.id) return;
    const fetchProjects = async () => {
      setProjectsLoading(true);
      setProjectsError(null);
      try {
        const data = await getBusinessProjects(businessData.id);
        setProjects(data);
      } catch (err) {
        setProjectsError(t('errors.fetchProjects'));
      } finally {
        setProjectsLoading(false);
      }
    };
    fetchProjects();
  }, [businessData?.id, t]);

  // useEffect para cargar empleados
  useEffect(() => {
    if (!businessData?.id) return;
    const fetchEmployees = async () => {
      setEmployeesLoading(true);
      try {
        const data = await getBusinessEmployees(businessData.id);
        setEmployees(data);
      } catch (err) {
        // setEmployeesError(t('errors.fetchEmployees')); // Removed as per edit hint
      } finally {
        setEmployeesLoading(false);
      }
    };
    fetchEmployees();
  }, [businessData?.id, t]);

  // Obtener workers asignados a proyectos
  useEffect(() => {
    const fetchWorkers = async () => {
      // Extraer todos los UIDs de assignedTo en tasks de todos los proyectos
      const assignedUids = new Set<string>();
      projects.forEach((project: any) => {
        if (Array.isArray(project.tasks)) {
          project.tasks.forEach((task: any) => {
            if (task.assignedTo) assignedUids.add(task.assignedTo);
          });
        }
      });
      // Quitar los que ya están en empleados
      const employeeUids = new Set(employees.map(e => e.id));
      const uniqueWorkerUids = Array.from(assignedUids).filter(uid => !employeeUids.has(uid));
      // Obtener datos de usuario para cada UID
      const db = getFirestore();
      const workerData = await Promise.all(uniqueWorkerUids.map(async (uid) => {
        const userDoc = await getDoc(doc(db, 'users', uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          return {
            id: uid,
            firstName: data.firstName,
            lastName: data.lastName,
            photoURL: data.photoURL,
            role: 'worker',
          };
        }
        return null;
      }));
      setWorkers(workerData.filter(Boolean));
    };
    if (projects.length > 0) fetchWorkers();
  }, [projects, employees]);

  // Buscar proyectos y tareas asignadas a la persona seleccionada
  useEffect(() => {
    if (!selectedPerson) return;
    // Agrupar tareas por proyecto
    const assignments: any[] = [];
    projects.forEach((project: any) => {
      if (Array.isArray(project.tasks)) {
        const userTasks = project.tasks.filter((task: any) => task.assignedTo === selectedPerson.id);
        if (userTasks.length > 0) {
          assignments.push({
            projectId: project.id,
            projectName: project.name,
            tasks: userTasks,
          });
        }
      }
    });
    setPersonAssignments(assignments);
  }, [selectedPerson, projects]);

  const handleBellClick = async () => {
    setPopoverOpen((open) => !open);
    if (!popoverOpen && notifications.length > 0 && businessData?.businessId) {
      const db = getFirestore();
      const notifRef = collection(db, 'notifications', businessData.businessId, 'notifications');
      for (const notif of notifications) {
        await updateDoc(doc(notifRef, notif.id), { read: true });
      }
    }
  };

  const getTaskStatusLabel = (columnId: string, t: any) => {
    switch (columnId) {
      case 'new': return t('business.taskStatusNew');
      case 'in_progress': return t('business.taskStatusInProgress');
      case 'done': return t('business.taskStatusDone');
      default: return columnId;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100 flex items-center justify-center">
        <div className="bg-red-50 border border-red-200 rounded-3xl p-8 max-w-md shadow-2xl">
          <p className="text-red-700 text-center">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100 p-8 font-sans">
      <div className="max-w-7xl mx-auto">
        {/* Header del Dashboard */}
        <div className="bg-white rounded-3xl p-10 mb-10 shadow-2xl border border-white/40">
          <div className="flex items-center gap-6">
            <div className="flex-shrink-0">
              {businessData?.logo ? (
                <img 
                  src={businessData.logo} 
                  alt={businessData.companyName} 
                  className="w-20 h-20 rounded-2xl object-cover shadow-md border-2 border-white/60"
                />
              ) : (
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-red-500 via-pink-400 to-red-600 flex items-center justify-center text-white text-2xl font-bold shadow-md border-2 border-white/60">
                  {businessData?.companyName?.charAt(0) || 'B'}
                </div>
              )}
            </div>
            <div className="flex-1">
              <h1 className="text-4xl font-extrabold text-gray-900 mb-2 tracking-tight drop-shadow-lg">
                <span className="bg-gradient-to-r from-red-500 via-pink-400 to-red-600 bg-clip-text text-transparent">
                  {businessData?.companyName}
                </span>
              </h1>
              <p className="text-gray-600 mb-2">
                {businessData?.businessIdType}: {businessData?.businessId}
              </p>
              <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${
                businessData?.status === 'active' 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                {t(`business.statuses.${businessData?.status?.toLowerCase() || 'pending'}`)}
              </span>
            </div>
            <div className="relative">
              <button ref={bellRef} onClick={handleBellClick} className="text-gray-500 hover:text-gray-700 relative">
              <FaBell className="h-6 w-6" />
              {notifications.length > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full text-xs px-1 py-0.5">
                  {notifications.length}
                </span>
              )}
            </button>
              
              {/* Popover de notificaciones */}
              {popoverOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 overflow-y-auto">
                  <div className="p-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">{t('notifications.title') || 'Notificaciones'}</h3>
                  </div>
                  <div className="p-2">
                    {notifications.length === 0 ? (
                      <div className="text-center py-4 text-gray-500">
                        {t('notifications.noNotifications') || 'No hay notificaciones'}
                      </div>
                    ) : (
                      notifications.map((notification) => (
                        <NotificationItem 
                          key={notification.id} 
                          notification={notification} 
                          onClose={() => setPopoverOpen(false)}
                        />
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Contenido del Dashboard */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
          {/* Información de Contacto */}
          <div className="bg-white rounded-3xl shadow-2xl border border-white/40 p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6">{t('business.contactInfo')}</h2>
            <div className="space-y-4">
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-600 mb-1">{t('business.address')}</label>
                <p className="text-gray-900">{businessData?.address || '-'}</p>
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-600 mb-1">{t('business.phone')}</label>
                <p className="text-gray-900">{businessData?.phone || '-'}</p>
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-600 mb-1">{t('business.email')}</label>
                <p className="text-gray-900">{businessData?.businessEmail || '-'}</p>
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-600 mb-1">{t('business.employeeCount')}</label>
                <p className="text-gray-900">{businessData?.employeeCount || '-'}</p>
              </div>
            </div>
          </div>

          {/* Proyectos Reales */}
          <div className="bg-white rounded-3xl shadow-2xl border border-white/40 p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6">{t('business.recentProjects')}</h2>
            <div className="space-y-4">
              {projectsLoading ? (
                <div className="text-center text-gray-400">{t('common.loading')}</div>
              ) : projectsError ? (
                <div className="text-center text-red-500">{projectsError}</div>
              ) : projects.length === 0 ? (
                <div className="bg-white rounded-2xl p-10 text-center shadow-2xl border border-white/40">
                  <div className="max-w-md mx-auto">
                    <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <FaProjectDiagram className="text-3xl text-gray-400" />
                    </div>
                    <div className="text-gray-400 text-lg font-semibold">{t('business.noProjects')}</div>
                  </div>
                </div>
              ) : (
                <>
                  {projects.slice(0, 3).map(project => {
                    // Usar valores por defecto si faltan campos
                    const name = project.name || 'Proyecto sin nombre';
                    const status = project.status || 'Sin estado';
                    let progress = 0;
                    if (typeof project.progress === 'number') {
                      progress = project.progress;
                    } else if (typeof project.progress === 'string' && !isNaN(Number(project.progress))) {
                      progress = Number(project.progress);
                    }
                    return (
                      <div
                        key={project.id}
                        className="border border-gray-200 rounded-2xl p-4 bg-white/80 shadow-sm hover:shadow-lg transition-all cursor-pointer hover:bg-red-50"
                        onClick={() => navigate(`/projects/${project.id}`)}
                        tabIndex={0}
                        role="button"
                        aria-label={name}
                        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') navigate(`/projects/${project.id}`); }}
                      >
                        <div className="flex justify-between items-start mb-3">
                          <h3 className="font-semibold text-gray-900">{name}</h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            status === 'Completado' 
                              ? 'bg-green-100 text-green-800'
                              : status === 'En progreso'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {status}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                          <div 
                            className="bg-red-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${progress}%` }}
                          ></div>
                        </div>
                        <p className="text-sm text-gray-600">{progress}%</p>
                      </div>
                    );
                  })}
                  {projects.length > 3 && (
                    <div className="flex justify-center mt-4">
                      <button
                        onClick={() => navigate('/projects')}
                        className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200"
                      >
                        {t('common.viewAll', 'Ver todos')}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Clientes Reales */}
          <div className="bg-white rounded-3xl shadow-2xl border border-white/40 p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6">{t('business.recentClients')}</h2>
            <div className="space-y-4">
              {clientsLoading ? (
                <div className="text-center text-gray-400">{t('common.loading')}</div>
              ) : clientsError ? (
                <div className="text-center text-red-500">{clientsError}</div>
              ) : clients.length === 0 ? (
                <div className="bg-white rounded-2xl p-10 text-center shadow-2xl border border-white/40">
                  <div className="max-w-md mx-auto">
                    <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <FaUserFriends className="text-3xl text-gray-400" />
                    </div>
                    <div className="text-gray-400 text-lg font-semibold">{t('business.noClients')}</div>
                  </div>
                </div>
              ) : (
                <>
                  {clients.slice(0, 3).map(client => (
                <div key={client.id} className="border border-gray-200 rounded-2xl p-4 bg-white/80 shadow-sm hover:shadow-lg transition-all">
                  <h3 className="font-semibold text-gray-900 mb-2">{client.name}</h3>
                      <p className="text-sm text-gray-600">{client.email}</p>
                      <p className="text-sm text-gray-600">{client.phone}</p>
                      <p className="text-sm text-gray-600">{client.address}</p>
                      <p className="text-xs text-gray-400">
                        {client.createdAt?.toDate ? client.createdAt.toDate().toLocaleDateString() : ''}
                  </p>
                </div>
              ))}
                  {clients.length > 3 && (
                    <div className="flex justify-center mt-4">
                      <button
                        onClick={() => navigate('/business/clients')}
                        className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200"
                      >
                        {t('common.viewAll', 'Ver todos')}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Personal Real */}
          <div className="bg-white rounded-3xl shadow-2xl border border-white/40 p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6">{t('business.recentEmployees')}</h2>
            <div className="space-y-4">
              {employeesLoading ? (
                <div className="text-center text-gray-400">{t('common.loading')}</div>
              ) : employees.length === 0 && workers.length === 0 ? (
                <div className="bg-white rounded-2xl p-10 text-center shadow-2xl border border-white/40">
                  <div className="max-w-md mx-auto">
                    <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <FaUserTie className="text-3xl text-gray-400" />
                    </div>
                    <div className="text-gray-400 text-lg font-semibold">{t('business.noEmployees')}</div>
                  </div>
                </div>
              ) : (
                <>
                  {[...employees.map(e => ({...e, type: 'employee'})), ...workers.map(w => ({...w, type: 'worker'}))].map(person => (
                    <div
                      key={person.id}
                      className="border border-gray-200 rounded-2xl p-4 bg-white/80 shadow-sm hover:shadow-lg transition-all flex items-center gap-4 cursor-pointer hover:bg-red-50"
                      onClick={() => { setSelectedPerson(person); setModalOpen(true); }}
                      tabIndex={0}
                      role="button"
                      aria-label={person.firstName}
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { setSelectedPerson(person); setModalOpen(true); } }}
                    >
                      {/* Avatar */}
                      {person.photoURL ? (
                        <img src={person.photoURL} alt={person.firstName} className="w-12 h-12 rounded-full object-cover border-2 border-white shadow" />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-500 via-pink-400 to-red-600 flex items-center justify-center text-white text-xl font-bold border-2 border-white shadow">
                          {person.firstName?.charAt(0) || '?'}
                        </div>
                      )}
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-1">{person.firstName} {person.lastName}</h3>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-1 rounded-full font-semibold ${person.type === 'employee' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>{person.type === 'employee' ? t('business.badgeEmployee') : t('business.badgeWorker')}</span>
                          {person.role && person.type === 'employee' && (
                            <span className="text-xs text-red-600 font-medium">{person.role}</span>
                          )}
                        </div>
                        {person.joinDate && person.type === 'employee' && (
                          <p className="text-sm text-gray-600 mt-1">{person.joinDate?.toDate ? person.joinDate.toDate().toLocaleDateString() : ''}</p>
                        )}
                      </div>
                    </div>
                  ))}
                  {employees.length + workers.length > 3 && (
                    <div className="flex justify-center mt-4">
                      <button
                        onClick={() => navigate('/business/employees')}
                        className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200"
                      >
                        {t('common.viewAll', 'Ver todos')}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Ingresos y Gastos */}
          <div className="bg-white rounded-3xl shadow-2xl border border-white/40 p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6">{t('business.financialOverview')}</h2>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-4 bg-green-50 rounded-xl border border-green-100">
                <label className="text-sm font-medium text-gray-600">{t('business.income')}</label>
                <p className="text-2xl font-bold text-green-600">${financialData.income.toLocaleString()}</p>
              </div>
              <div className="flex justify-between items-center p-4 bg-red-50 rounded-xl border border-red-100">
                <label className="text-sm font-medium text-gray-600">{t('business.expenses')}</label>
                <p className="text-2xl font-bold text-red-600">${financialData.expenses.toLocaleString()}</p>
              </div>
              <div className="flex justify-between items-center p-4 bg-blue-50 rounded-xl border border-blue-100">
                <label className="text-sm font-medium text-gray-600">{t('business.balance')}</label>
                <p className="text-2xl font-bold text-blue-600">${financialData.balance.toLocaleString()}</p>
              </div>
            </div>
          </div>

          {/* Cumplimiento de Tareas */}
          <div className="bg-white rounded-3xl shadow-2xl border border-white/40 p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6">{t('business.taskCompletion')}</h2>
            <div className="flex flex-col items-center">
              <div className="relative w-32 h-32 mb-4">
                <svg viewBox="0 0 36 36" className="w-full h-full transform -rotate-90">
                  <path
                    className="text-gray-200"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                    d="M18 2.0845
                      a 15.9155 15.9155 0 0 1 0 31.831
                      a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                  <path
                    className="text-blue-600"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray={`${taskCompletion.percentage}, 100`}
                    d="M18 2.0845
                      a 15.9155 15.9155 0 0 1 0 31.831
                      a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-blue-600 text-lg font-semibold">{taskCompletion.percentage}%</span>
                </div>
              </div>
              <div className="text-center">
                <p className="text-gray-700">
                  {taskCompletion.completed} {t('business.completed')} {t('business.of')} {taskCompletion.total} {t('business.tasks')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Modal de detalles de persona seleccionada */}
      {modalOpen && selectedPerson && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-lg w-full relative border border-white/30 animate-fadeInUp">
            <button
              className="absolute top-4 right-4 text-2xl text-gray-400 hover:text-red-600"
              onClick={() => setModalOpen(false)}
              aria-label="close"
            >
              <FaTimesCircle />
            </button>
            <div className="flex flex-col items-center mb-6">
              {selectedPerson.photoURL ? (
                <img src={selectedPerson.photoURL} alt={selectedPerson.firstName} className="w-20 h-20 rounded-full object-cover border-2 border-white shadow mb-2" />
              ) : (
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-red-500 via-pink-400 to-red-600 flex items-center justify-center text-white text-3xl font-bold border-2 border-white shadow mb-2">
                  {selectedPerson.firstName?.charAt(0) || '?'}
                </div>
              )}
              <h3 className="font-bold text-xl text-gray-900 mb-1">{selectedPerson.firstName} {selectedPerson.lastName}</h3>
              <span className={`text-xs px-2 py-1 rounded-full font-semibold ${selectedPerson.type === 'employee' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>{selectedPerson.type === 'employee' ? t('business.badgeEmployee') : t('business.badgeWorker')}</span>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-4">{t('business.assignedProjects')}</h4>
            {personAssignments.length === 0 ? (
              <div className="text-gray-500 text-center">{t('business.noAssignedProjects')}</div>
            ) : (
              <div className="space-y-4 max-h-80 overflow-y-auto pr-2">
                {personAssignments.map(assignment => (
                  <div key={assignment.projectId} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <h5 className="font-semibold text-red-600 mb-2">{assignment.projectName}</h5>
                    <ul className="list-disc pl-5 space-y-1">
                      {assignment.tasks.map((task: any) => (
                        <li key={task.id} className="text-gray-700 flex flex-col md:flex-row md:items-center md:gap-2">
                          <Link
                            to={`/projects/${assignment.projectId}`}
                            className="font-medium text-blue-700 hover:underline hover:text-blue-900 transition-colors"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {task.title || t('business.noTaskTitle')}
                          </Link>
                          {/* Estado */}
                          <span className={`ml-2 text-xs px-2 py-0.5 rounded-full font-semibold ${
                            task.columnId === 'done' ? 'bg-green-100 text-green-700' :
                            task.columnId === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {getTaskStatusLabel(task.columnId, t)}
                          </span>
                          {/* Fecha de inicio */}
                          {task.startDate && (
                            <span className="ml-2 text-xs text-gray-500">
                              {(() => {
                                let dateObj = null;
                                if (typeof task.startDate === 'string' || typeof task.startDate === 'number') {
                                  dateObj = new Date(task.startDate);
                                } else if (task.startDate && typeof task.startDate === 'object' && 'seconds' in task.startDate) {
                                  dateObj = new Date(task.startDate.seconds * 1000);
                                }
                                return dateObj && !isNaN(dateObj.getTime())
                                  ? dateObj.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
                                  : '';
                              })()}
                            </span>
                          )}
                          {task.description && <span className="text-xs text-gray-400 ml-2">{task.description}</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessDashboard;